import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function ValutazioneDettaglio() {
  const { id, reviewId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get(`/staff/${id}`),
          api.get(`/staff/${id}/reviews/${reviewId}`),
        ]);
        if (!alive) return;
        setStaff(s.data);
        setReview(r.data);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate(`/staff/${id}`, { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, reviewId, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento valutazione…
      </div>
    );
  }
  if (!review || !staff) return null;

  const dataLabel = new Date(review.data_valutazione).toLocaleDateString("it-IT", {
    day: "2-digit", month: "long", year: "numeric",
  });

  return (
    <div className="ih-anim-in -mx-4 sm:-mx-6 lg:-mx-8 -my-4 sm:-my-6 lg:-my-8" data-testid="valutazione-dettaglio-page">
      {/* Header sticky */}
      <header
        className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border/60"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/staff/${id}`)}
              className="h-9 w-9 -ml-2 rounded-lg hover:bg-accent flex items-center justify-center shrink-0"
              data-testid="back-btn"
              aria-label="Indietro"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Valutazione del {dataLabel}</div>
              <h1 className="font-display text-lg sm:text-xl tracking-tight truncate">
                {staff.nome} {staff.cognome} · <span className="text-muted-foreground font-normal">{review.periodo}</span>
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-2xl w-full mx-auto px-4 sm:px-6 py-6">
        {/* Riepilogo */}
        <div className="rounded-2xl border border-border/60 bg-card/60 p-5 mb-6" data-testid="review-summary">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Media valutazione</div>
              <div className="font-display text-4xl sm:text-5xl mt-1" data-testid="review-media">
                {review.punteggio_medio}<span className="text-lg text-muted-foreground font-normal">/10</span>
              </div>
            </div>
            <div className="text-sm text-right">
              <div className="text-muted-foreground text-xs">Valutatore</div>
              <div className="font-medium">{review.valutatore_nome}</div>
              <div className="text-muted-foreground text-xs mt-1">{dataLabel}</div>
            </div>
          </div>
        </div>

        {/* Risposte */}
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Risposte</div>
        <ul className="divide-y divide-border/60" data-testid="review-responses">
          {review.risposte.map((r, idx) => (
            <li key={r.domanda_id} className="py-4" data-testid={`r-${idx}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[15px] leading-snug font-medium flex-1 min-w-0">
                  <span className="text-muted-foreground mr-1.5">{idx + 1}.</span>{r.domanda_label}
                </span>
                <span className="text-lg font-semibold text-lavender-600 dark:text-lavender-400 tabular-nums whitespace-nowrap">
                  {r.punteggio}<span className="text-[11px] text-muted-foreground font-normal ml-0.5">/10</span>
                </span>
              </div>
              {r.commento && (
                <div className="mt-1.5 text-sm text-muted-foreground italic leading-relaxed">
                  “{r.commento}”
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Commento generale */}
        {review.commento_generale && (
          <div className="mt-6 rounded-xl bg-accent/40 border border-border/40 p-4">
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5">Commento generale</div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap">{review.commento_generale}</div>
          </div>
        )}
      </div>
    </div>
  );
}
